/**
 * LeadershipPage — the *human* leadership team behind NoJo Enterprise.
 *
 * Counterpart to `OurTeamPage` (which showcases the 5-member AI Operations
 * process). Source data: `data/humanTeam.js` — 4 humans:
 *   • Adam Jones   (Founder & CEO)        — gets hero treatment
 *   • Ronald Jones (Sales Director)
 *   • Genichi Hirayama (Branch Manager)
 *   • Naoko Urashima (Sales Associate)
 *
 * Top of page hosts the unified `PeopleStoryTabs` so visitors can pivot
 * between AI Team ⇄ Leadership ⇄ Founder's Messages without going back
 * to the menu.
 */
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Briefcase, Calendar } from "lucide-react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";
import PageSideAura from "@/components/motion/PageSideAura";
import Reveal from "@/components/motion/Reveal";
import PeopleStoryTabs from "@/components/PeopleStoryTabs";
import SourceFreshnessBadge from "@/components/SourceFreshnessBadge";
import { HUMAN_TEAM, HUMAN_TEAM_STATS } from "@/data/humanTeam";
import useCustomerCount from "@/lib/useCustomerCount";

const PURPLE = "#5B2E83";

function pickL(en, ja, lang) {
  return lang === "ja" ? ja : en;
}

// Color theme per stat-strip card. Index matches HUMAN_TEAM_STATS order:
//   0 = Years in Business    → purple
//   1 = Team Members         → blue
//   2 = Military Installs    → green
//   3 = Customers Served     → orange/gold
const STAT_THEMES = [
  { bg: "#F4EEF9", ring: "#E9D8F4", label: "#5B2E83" },
  { bg: "#EEF6FF", ring: "#D6E8FB", label: "#0E5FB0" },
  { bg: "#ECF6EC", ring: "#CDE7CC", label: "#2F8F3C" },
  { bg: "#FFF4E6", ring: "#FAD9A6", label: "#B86E0F" },
];

export default function LeadershipPage() {
  const { lang = "en" } = useParams();
  const isJa = lang === "ja";
  // Live JEC-only customer count — used by the 4-up stat strip below the
  // founder section. Index 3 of HUMAN_TEAM_STATS ("Customers Served") gets
  // its `value` swapped in at render time so the page always reflects the
  // current JapanETCcard.com member count.
  const customerCount = useCustomerCount({ partner: "japanetccard" });
  const founder = HUMAN_TEAM.find((m) => m.isFounder);
  const others = HUMAN_TEAM.filter((m) => !m.isFounder);

  return (
    <article className="bg-white" data-testid="leadership-page">
      <SEO {...PAGE_SEO.ourLeaders[lang]} path={`/${lang}/our-leaders`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "経営・運営チーム" : "Leadership", url: `/${lang}/our-leaders` },
        ])}
      />

      <PeopleStoryTabs lang={lang} />

      {/* ===== HERO (success-led narrative — owner directive: frame the
              leadership team as the *reason* NoJo Enterprise has succeeded,
              not just "the real humans"). Feb 2026 update: centered
              headline at the top of the page (no eyebrow, no stat strip),
              two supporting paragraphs running the full content width. ===== */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <h1
            data-testid="leadership-page-h1"
            className={[
              isJa ? "font-ja" : "font-display",
              "text-center text-[#0F1D2D] text-3xl md:text-[42px] lg:text-[52px] font-bold tracking-tighter leading-[1.05]",
            ].join(" ")}
          >
            {isJa
              ? "私たちの強さは、リーダーシップチームそのものです。"
              : "Our success is the leadership team."}
          </h1>
          <p className="mt-7 text-[16px] md:text-[18px] leading-[1.85] text-[#334155]">
            {isJa
              ? "NoJo Enterpriseが13年以上にわたって日本のお客様から信頼をいただけてきた最大の理由は、長年にわたり一緒に仕事を積み重ねてきた、この少数精鋭のリーダーシップチームにあります。営業、運営、現場サポート、そして日本国内のパートナーシップ ─ それぞれの責任領域を、迷いなく、しかも互いに連携しながら回せること。これが私たちの最大の競争力です。同じメンバーが何年も一緒に動いてきたからこそ、判断のスピード、現場での対応力、そして日本側のパートナーとの信頼関係が成立します。"
              : "The reason NoJo Enterprise has earned the trust of customers across Japan for over thirteen years is this small, deeply experienced leadership team — people who have operated together for years and know exactly how to move sales, operations, on-the-ground support, and Japan-side partnerships forward without friction. That alignment is our single biggest competitive advantage, and it is the reason every customer interaction — from the first inquiry to thirteen years of continuous service — feels consistent, accountable, and human."}
          </p>
          <p className="mt-4 text-[15px] md:text-[16px] leading-[1.85] text-[#475569]">
            {isJa
              ? "業界の壁を切り拓き、外国人居住者・米軍関係者・各国大使館・国際企業の皆様に日本でのETC利用を支援し、2026年6月には18,000名を超えるお客様の信頼に到達するという大きな節目を迎えました。その実績の一つひとつが、このチームの判断と実行力によって積み上げられたものです。日本全国37の米軍施設を含むネットワークを、少人数のチームで十年以上にわたり運営し続けています。次世代のJapanETCcardと、2026年後半に予定されているJapanTollReceiptsの新プラットフォーム開発も、同じチームで進行中です。"
              : "Breaking through the early industry resistance, opening ETC access for foreign residents, U.S. military families, embassies, and international companies, and reaching 18,000+ customers nationwide in June 2026 — a major milestone. Every step behind those numbers was carried by the judgment and execution of this team. A small team has run this network — including 37 U.S. military installations across Japan — for more than a decade without interruption, and the operating discipline behind that consistency is what every new customer benefits from on day one. The same team is now driving development for the next-generation JapanETCcard platform and the enhanced JapanTollReceipts platform expected to launch later in 2026."}
          </p>
        </div>
      </section>

      {/* ===== FOUNDER SPOTLIGHT =====
            Owner directive Feb 2026 (round 2): step away from both the
            generic dark-navy and the earlier plum. Using a deep slate
            (#172033) with a layered teal + purple glow to give the
            founder section its own atmosphere distinct from the rest of
            the site. */}
      {founder && (
        <section className="text-white relative overflow-hidden" style={{ backgroundColor: "#172033" }}>
          <span
            aria-hidden
            className="absolute -right-32 -top-32 w-[460px] h-[460px] rounded-full opacity-25 blur-3xl"
            style={{ backgroundColor: "#1FA3B2" }}
          />
          <span
            aria-hidden
            className="absolute -left-32 -bottom-32 w-[420px] h-[420px] rounded-full opacity-20 blur-3xl"
            style={{ backgroundColor: "#8C5BD9" }}
          />
          <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-4">
                <Reveal variant="up">
                  <div
                    className="relative w-full max-w-[340px] aspect-[3/4] rounded-2xl overflow-hidden ring-2"
                    style={{ borderColor: PURPLE, boxShadow: `0 24px 60px -30px ${PURPLE}` }}
                    data-testid="leadership-founder-portrait"
                  >
                    <img
                      src={founder.portrait}
                      alt={`${founder.nameEn} — ${founder.titleEn}`}
                      className="block w-full h-full object-cover"
                      loading="eager"
                    />
                    <span
                      className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold"
                      style={{ backgroundColor: PURPLE, color: "#fff" }}
                    >
                      {isJa ? "創業者 ・ CEO" : "Founder & CEO"}
                    </span>
                  </div>
                </Reveal>
              </div>
              <div className="lg:col-span-8">
                <Reveal variant="up">
                  <div
                    className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold mb-2"
                    style={{ color: "#A9D5F2" }}
                  >
                    {isJa ? "リーダーシップ" : "Founder · Visionary · Builder"}
                  </div>
                  <h2
                    className={[
                      isJa ? "font-ja" : "font-display",
                      "text-white text-3xl md:text-[44px] font-bold tracking-tighter leading-[1.05]",
                    ].join(" ")}
                  >
                    {pickL(founder.nameEn, founder.nameJa, lang)}
                  </h2>
                  {/* Title — lifted from low-contrast purple (#5B2E83) on
                      navy to a brighter lavender (#C9B0EA) so it reads
                      cleanly. Owner directive Feb 2026: "the purple
                      doesn't stand out very well on darker pages." */}
                  <div
                    className="mt-2 text-[14px] tracking-[0.18em] font-mono-display uppercase font-semibold"
                    style={{ color: "#C9B0EA" }}
                  >
                    {pickL(founder.titleEn, founder.titleJa, lang)}
                  </div>
                  {/* Ongoing role pill — owner directive Feb 2026: surface
                      Adam's active "Technical & AI development lead" role
                      on the profile so the present-tense work shows
                      alongside the static "Founder & CEO" title. */}
                  <div
                    data-testid="leadership-founder-current-role"
                    className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] tracking-[0.18em] font-mono-display uppercase font-bold ring-1"
                    style={{
                      color: "#7DD8E0",
                      backgroundColor: "rgba(31, 163, 178, 0.12)",
                      borderColor: "rgba(125, 216, 224, 0.35)",
                    }}
                  >
                    <span
                      aria-hidden
                      className="h-1.5 w-1.5 rounded-full animate-pulse"
                      style={{ backgroundColor: "#7DD8E0" }}
                    />
                    {isJa
                      ? "技術・AI開発リード ─ 継続中"
                      : "Technical & AI Development Lead · Ongoing"}
                  </div>
                  <p className="mt-5 text-[15.5px] leading-[1.85] text-white/85 max-w-3xl">
                    {pickL(founder.bioEn, founder.bioJa, lang)}
                  </p>

                  <div className="mt-7 flex flex-wrap gap-3">
                    <Link
                      to={`/${lang}/founders-message/japanetccard`}
                      data-testid="leadership-founder-cta-jec"
                      className="inline-flex items-center gap-2 bg-white text-[#0F1D2D] px-5 py-3 rounded-full text-[13px] font-semibold hover:bg-[#F1F5F9] transition-colors"
                    >
                      {isJa
                        ? "代表メッセージを読む・JapanETCcard"
                        : "Read Founder's Message · JapanETCcard"}
                      <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                    </Link>
                    <Link
                      to={`/${lang}/founders-message/japantollreceipts`}
                      data-testid="leadership-founder-cta-jtr"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] font-semibold ring-1 ring-white/30 text-white hover:bg-white/10 transition-colors"
                    >
                      {isJa
                        ? "代表メッセージを読む・JapanTollReceipts"
                        : "Read Founder's Message · JapanTollReceipts"}
                    </Link>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== STATS — colored cards, customer count pulled live from
              the JapanETCcard API (owner directive Feb 2026). ===== */}
      <section className="relative isolate bg-white border-b border-[#E2E8F0]">
        <PageSideAura testId="leadership-stats-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-12">
          <ul
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="leadership-stat-cards"
          >
            {HUMAN_TEAM_STATS.map((s, i) => {
              const theme = STAT_THEMES[i % STAT_THEMES.length];
              const isCustomerCard = s.labelEn === "Customers Served";
              const displayValue = isCustomerCard
                ? customerCount.value
                : pickL(s.valueEn, s.valueJa, lang);
              return (
                <li
                  key={i}
                  className="rounded-2xl px-5 py-5 ring-1"
                  style={{ backgroundColor: theme.bg, borderColor: theme.ring, boxShadow: "none" }}
                  data-testid={`leadership-stat-${i}`}
                >
                  <div className="font-display text-[#0F1D2D] text-[26px] md:text-[32px] font-bold tracking-tight leading-none">
                    {displayValue}
                  </div>
                  <div
                    className="mt-2 text-[10.5px] tracking-[0.18em] font-mono-display uppercase font-bold leading-snug"
                    style={{ color: theme.label }}
                  >
                    {pickL(s.labelEn, s.labelJa, lang)}
                    {isCustomerCard && customerCount.isLive && (
                      <span
                        data-testid="leadership-stat-live-dot"
                        className="ml-1.5 inline-flex items-center gap-1 align-middle text-[#2F8F3C]"
                        title={isJa ? "ライブAPIから取得" : "Live from API"}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-[#2F8F3C] animate-pulse" />
                        {isJa ? "ライブ" : "LIVE"}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {customerCount.isLive && (
            <SourceFreshnessBadge
              sourceFreshness={customerCount.sourceFreshness.filter(
                (s) => s.serviceId === "japanetccard"
              )}
              lang={lang}
              className="mt-4"
            />
          )}
        </div>
      </section>

      {/* ===== OTHER LEADERS ===== */}
      <section className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="mb-10">
            <div className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold mb-2" style={{ color: PURPLE }}>
              {isJa ? "運営チーム" : "Leadership team"}
            </div>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-2xl md:text-[36px] font-bold tracking-tight leading-[1.1]",
              ].join(" ")}
            >
              {isJa
                ? "毎日の運営を担うチーム。"
                : "The people who run the day-to-day."}
            </h2>
          </div>

          <ul
            className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6"
            data-testid="leadership-team-members"
          >
            {others.map((m, i) => (
              <Reveal as="li" variant="up" delay={i * 80} key={m.slug}>
                <div
                  className="h-full flex flex-col rounded-2xl bg-white ring-1 ring-[#E2E8F0] overflow-hidden transition-all hover:-translate-y-0.5 hover:ring-transparent hover:shadow-[0_24px_50px_-25px_rgba(15,29,45,0.3)]"
                  data-testid={`leadership-member-${m.slug}`}
                >
                  <div className="relative aspect-square w-full overflow-hidden bg-[#F1F5F9]">
                    <img
                      src={m.portrait}
                      alt={`${pickL(m.nameEn, m.nameJa, lang)} — ${pickL(m.titleEn, m.titleJa, lang)}`}
                      className="block w-full h-full object-cover"
                      loading="lazy"
                    />
                    <span
                      aria-hidden
                      className="absolute top-0 left-0 right-0 h-[3px]"
                      style={{ backgroundColor: PURPLE }}
                    />
                  </div>
                  <div className="p-5 md:p-6 flex-1 flex flex-col">
                    <div className="font-display text-[20px] font-bold tracking-tight text-[#0F1D2D] leading-snug">
                      {pickL(m.nameEn, m.nameJa, lang)}
                    </div>
                    <div
                      className="mt-1 text-[11px] tracking-[0.18em] font-mono-display uppercase font-semibold"
                      style={{ color: PURPLE }}
                    >
                      {pickL(m.titleEn, m.titleJa, lang)}
                    </div>
                    {m.currentRoleEn && (
                      <div
                        data-testid={`leadership-member-current-${m.slug}`}
                        className="mt-2.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ring-1 text-[10px] tracking-[0.16em] font-mono-display uppercase font-bold"
                        style={{
                          color: "#0E5FB0",
                          backgroundColor: "rgba(14, 95, 176, 0.08)",
                          borderColor: "rgba(14, 95, 176, 0.22)",
                        }}
                      >
                        <span
                          aria-hidden
                          className="h-1.5 w-1.5 rounded-full bg-[#0E5FB0] animate-pulse"
                        />
                        {pickL(m.currentRoleEn, m.currentRoleJa, lang)}
                      </div>
                    )}
                    <p className="mt-4 text-[13.5px] leading-[1.8] text-[#475569] flex-1">
                      {pickL(m.bioEn, m.bioJa, lang)}
                    </p>
                    {m.achievements?.length > 0 && (
                      <ul className="mt-4 space-y-1.5 pt-4 border-t border-[#F1F5F9]">
                        {m.achievements.map((a, j) => (
                          <li key={j} className="flex items-start gap-2 text-[12px] leading-[1.6] text-[#475569]">
                            <Briefcase
                              className="h-3.5 w-3.5 shrink-0 mt-0.5"
                              style={{ color: PURPLE }}
                              strokeWidth={2}
                            />
                            <span>{pickL(a.titleEn, a.titleJa, lang)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== CROSS-LINK TO AI TEAM ===== */}
      <section className="relative isolate bg-white">
        <PageSideAura testId="leadership-cta-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="rounded-2xl ring-1 ring-[#E2E8F0] bg-[#F8F9FA] p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
            <Calendar className="h-7 w-7 shrink-0" style={{ color: PURPLE }} strokeWidth={1.6} />
            <div className="flex-1">
              <h3 className="font-display text-[20px] font-bold tracking-tight text-[#0F1D2D]">
                {isJa
                  ? "AI支援ワークフローと連携しながら、お客様対応と運用を支えています。"
                  : "Together with our AI Operations team, we support customers 24/7."}
              </h3>
              <p className="mt-1.5 text-[13.5px] leading-[1.75] text-[#475569]">
                {isJa
                  ? "5つのAI支援プロセスが、人のチームの作業を支えています。"
                  : "A five-member AI operations process supports the work of the human team."}
              </p>
            </div>
            <Link
              to={`/${lang}/our-team`}
              data-testid="leadership-cta-ai-team"
              className="inline-flex items-center gap-2 text-white px-5 py-3 rounded-full text-[13px] font-semibold tracking-tight hover:opacity-90"
              style={{ backgroundColor: PURPLE }}
            >
              {isJa ? "AIチームを見る" : "Meet the AI Team"}
              <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
